import React from "react";
import { Styled } from "./styles";
import useUser from "@/context/user";
import {
    Avatar,
    Box,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { useMenu } from "./hooks";
import { stringToColor } from "@/utils/helpers";
import { i18n } from "@/utils/i18n";
import LanguagePicker from "@/components/common/language-picker";
import { Button } from "@aidergroup/ui-v2";
import { useLocation, useNavigate } from "react-router-dom";
import useFormStatus from "@/context/form";
import useAlert from "@/components/common/modals/alert/context";

const Header: React.FC = () => {
    const email = useUser(state => state.email);
    const { onLogout, onProfile, onOpenMenu, open, anchorEl, setAnchorEl } = useMenu();
    const { isDirty, isSubmitting, isSaveDisabled } = useFormStatus();
    const openAlert = useAlert(state => state.open);

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const onCancel = () => {
        if (isDirty) {
            return openAlert({
                title: i18n.t("alert.form.title"),
                message: i18n.t("alert.form.message"),
                actions: { confirm: { callback: () => navigate(-1) } },
            });
        }

        navigate(-1);
    };
    const isReportPage = /^\/organizations\/\w+\/submissions\/\w+\/reports\/\w+$/gi.test(pathname);
    const isCategoryPage =
        /^\/organizations\/\w+\/submissions\/\w+\/reports\/\w+\/categories-overview$/gi.test(
            pathname,
        );
    const formId = isCategoryPage ? "category-form" : "report-form";

    return (
        <Styled.Container>
            <LanguagePicker />
            <Box display="flex" alignItems="center" gap={4}>
                {isReportPage || isCategoryPage ? (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button
                            onClick={onCancel}
                            variant="outlined"
                            sx={{ borderRadius: 5 }}
                            disabled={isSubmitting}
                        >
                            {i18n.t("button.cancel")}
                        </Button>
                        <Tooltip
                            title={
                                isSaveDisabled
                                    ? i18n.t("messages.info.saveDisabledReviewNotes")
                                    : ""
                            }
                            disableHoverListener={!isSaveDisabled}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    sx={{ borderRadius: 5 }}
                                    type="submit"
                                    form={formId}
                                    disabled={isSubmitting || !isDirty || isSaveDisabled}
                                >
                                    {i18n.t("button.save")}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                ) : null}
                {!email ? null : (
                    <Styled.User>
                        <Avatar sx={{ backgroundColor: stringToColor(email ?? "u") }}>
                            {email[0].toUpperCase()}
                        </Avatar>
                        <Typography marginLeft={1} variant="caption">
                            {email}
                        </Typography>
                        <MoreVertIcon
                            id="menu-icon"
                            style={{ cursor: "pointer" }}
                            onClick={onOpenMenu}
                            aria-controls={open ? "user-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                        />
                        <Menu
                            id="user-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={() => setAnchorEl(null)}
                            MenuListProps={{ "aria-labelledby": "menu-icon" }}
                        >
                            <MenuItem onClick={onProfile}>
                                <ListItemIcon>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography variant="subtitle1">
                                        {i18n.t("navigation.profile")}
                                    </Typography>
                                </ListItemText>
                            </MenuItem>
                            <MenuItem onClick={onLogout}>
                                <ListItemIcon sx={{ paddingLeft: "2.5px" }}>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography variant="subtitle1">
                                        {i18n.t("navigation.logout")}
                                    </Typography>
                                </ListItemText>
                            </MenuItem>
                        </Menu>
                    </Styled.User>
                )}
            </Box>
        </Styled.Container>
    );
};

export default Header;
